// src/components/DischargeSummaryChat.tsx
// AI-assisted Discharge Summary drafting for IP discharge.
//
// v3 conversational layout (docs/HMS_CONVERSATIONAL_AI.md §6's own checklist
// for bringing a second screen onto the Consultation Notes v3 pattern,
// 2026-09-30): a resizable assistant column (left) + note canvas (right),
// replies stream straight into the note, a change ribbon states what
// changed after every turn with single-level Undo, per-section ↻
// regeneration, and "Promote as hospital template" for the Discharge
// Summary AI Assistant Role profile. No queue_id to persist per-message
// against (IP admissions don't have a queue entry), so the transcript here
// is in-memory for this sitting only. Approving still saves through the
// same POST /api/consultation/save used by OP consultations (queue_id: null
// is already supported there), so the generated document lands in the
// patient's record the same way.
//
// Deliberately NOT ported from ConsultationChat.tsx (see
// docs/HMS_CONVERSATIONAL_AI.md §6's "build fresh" list, and CLAUDE.md's
// Conversational Documentation section, which already flags this as a known
// gap): there is no capturedDataSkeleton.js/GET /api/consultation/start
// equivalent for an admission, so this screen has no deterministic "start
// from captured data" no-AI skeleton or per-line ⓘ source markers/Insert
// menu — buildCriticalInfoSummary/buildCaseSheetInfoSummary below (already
// existed pre-v3) remain the one context-assembly path, shown once in the
// "Patient information on file" panel and folded into the greeting. The
// completeness-nudges engine (utils/docCompleteness.js) is also
// Consultation-Notes-specific, so the AI-off assistant-column state shows a
// plain "nothing to flag" message here rather than a real checklist.
import { useState, useEffect, useRef, useMemo } from 'react';
import { loadConfig, getActiveProvider, getActiveModel, type AIMessage } from '../aiConfig';
import { getAuthUser, saveConsultation, getAiSettings, promoteConsultationToProfile, getDischargeMessages, addConsultationMessage } from '../api';
import { useResizableWidth } from '../hooks/useResizableWidth';
import DocumentPreviewButtons from './DocumentPreviewButtons';
import { Button } from './ui/Button';
import { fetchCaseSheet } from '../features/ip-case-sheet/api';
import type { CaseSheet } from '../features/ip-case-sheet/types';
import { cleanAIDraft, splitDraftReply, stripTitleEcho, MANUAL_MARKER } from '../lib/aiDraftCleanup';
import DocumentEditor, { type InsertableValue, type DocumentEditorHandle } from '../features/ai-assist/DocumentEditor';
import type { HeadingSection } from '../features/ai-assist/docAssist';
import { spliceSectionFromReply, diffSections, type SectionChange } from '../features/ai-assist/documentSections';
import { generateDocument, type GenerationSuggestion, type AppliedMemoryItem } from '../features/ai-assist/streamGeneration';
import { AssistantColumn } from '../features/ai-assist/AssistantColumn';
import { ChangeRibbon } from '../features/ai-assist/ChangeRibbon';
import type { ChatMessage } from '../features/ai-assist/ConsultationTranscript';
import { MyAiPreferencesPanel } from '../features/ai-assist/MyAiPreferencesPanel';
import { createPreference, deletePreference, getNextProposal, type DoctorPreference } from '../features/ai-assist/memoryApi';
import { ProposalCard } from '../features/ai-assist/ProposalCard';
import { toast } from '../hooks/useToast';
import { ToastAction } from './ui/Toast';
import { formatDocDate, removeAllPlaceholders, unfilledFieldLabels } from '../features/ai-assist/smartFields';

export interface IPCharge {
  id: number;
  description: string;
  amount: number | string;
  quantity: number;
  charge_type?: string;
  gst_percent?: number | string;
  /** Set on a laboratory test charge — it follows its lab order and is
   * reversed by cancelling the test, not removable from the charges list. */
  lab_order_id?: number | null;
}

export interface AdmissionOriginVisit {
  vitals?: {
    bp_sys?: string | number; bp_dia?: string | number; pulse?: string | number;
    temp?: string | number; spo2?: string | number; bmi?: string | number;
    symptoms?: string;
  };
  pre_consult?: {
    ailment?: string; risk_level?: string; pain_scale?: string | number;
    allergies?: string; current_meds?: string; nurse_notes?: string;
  };
  consultation?: { content?: string; doc_type?: string };
}

export interface Admission {
  id: number;
  patient_id: number;
  patient_name: string;
  patient_uhid: string;
  age: number | null;
  gender: string;
  admitted_at: string;
  ward_name: string;
  bed_number: string | number;
  daily_rate: number;
  admitting_doctor?: string;
  provisional_diagnosis?: string;
  admission_notes?: string;
  discharge_date?: string;
  charges: IPCharge[];
  origin_visit?: AdmissionOriginVisit;
}

const DOC_TYPE = 'Discharge Summary' as const;

// The note↔assistant split — own key from Consultation Notes' own
// ('hms_consult_assistant_width'), per docs/HMS_CONVERSATIONAL_AI.md §6's
// "a new, distinct localStorage/preference key" instruction — these are two
// independent doctor preferences, not one shared split.
const ASSISTANT_WIDTH_KEY = 'hms_discharge_assistant_width';
const DEFAULT_ASSISTANT_WIDTH = 460;
const MIN_ASSISTANT_WIDTH = 340;
const MAX_ASSISTANT_WIDTH = 760;

// The system prompt asks the model to prefix a finished document with the
// exact "--- AI-ASSISTED DRAFT ---" line, but a follow-up turn ("Let's
// update the summary with that...") doesn't always include it even when the
// reply IS the full document — see the identical note in ConsultationChat.tsx,
// where this exact gap left a stale earlier draft silently re-approved
// instead of the doctor's actual latest, fuller note. Recognizing the
// discharge-summary section labels themselves is a second, independent way
// to catch "this reply is a document" that doesn't depend on the marker.
function looksLikeDocument(text: string): boolean {
  if (text.includes('AI-ASSISTED DRAFT') || text.includes('DISCHARGE')) return true;
  const sectionHits = ['diagnosis', 'treatment', 'follow-up', 'discharge medications', 'condition at discharge']
    .filter(k => new RegExp(`\\*{0,2}${k}\\*{0,2}:?`, 'i').test(text)).length;
  return sectionHits >= 2;
}

// "Fill from clinical data": sent as the doctor's own message so it is
// revised against the document as it stands (see send()'s conditional-
// instruction wrapping) — mirrors ConsultationChat.tsx's
// FILL_FROM_CLINICAL_DATA, scoped to what this screen actually has on file
// (buildCriticalInfoSummary/buildCaseSheetInfoSummary below).
const FILL_FROM_CLINICAL_DATA = 'Fill in this discharge summary from the information already on file for this stay: vitals, triage, the admitting doctor\'s note, the Case Sheet record and in-stay charges. Complete each field still to fill and each empty section only where that data supports it, and leave a field for me where it doesn\'t. Don\'t change anything I have already written.';

// Pulls together everything already on record for this stay — vitals,
// nurse pre-consult triage, and the doctor's own consultation note from the
// OP visit that led to this admission (see GET /api/ip/admissions/:id's
// origin_visit), plus the admission's own diagnosis/notes and in-stay
// charges — so the doctor reviews/corrects/adds instead of re-typing
// everything from scratch.
function buildCriticalInfoSummary(admission: Admission) {
  const ov = admission.origin_visit || {};
  const { vitals, pre_consult: preConsult, consultation } = ov;
  const lines: string[] = [];

  if (vitals && Object.keys(vitals).length) {
    lines.push(`**Vitals on admission:** BP ${vitals.bp_sys || '—'}/${vitals.bp_dia || '—'} mmHg · Pulse ${vitals.pulse || '—'} bpm · Temp ${vitals.temp || '—'}°F · SpO₂ ${vitals.spo2 || '—'}%${vitals.bmi ? ` · BMI ${vitals.bmi}` : ''}`);
    if (vitals.symptoms) lines.push(`**Presenting complaints:** ${vitals.symptoms}`);
  }
  if (preConsult && Object.keys(preConsult).length) {
    lines.push(`**Nurse triage:** Ailment: ${preConsult.ailment || 'General'} · Risk: ${preConsult.risk_level || '—'} · Pain ${preConsult.pain_scale || '—'}/10`);
    if (preConsult.allergies) lines.push(`**Allergies:** ${preConsult.allergies}`);
    if (preConsult.current_meds) lines.push(`**Medications on admission:** ${preConsult.current_meds}`);
    if (preConsult.nurse_notes) lines.push(`**Nurse notes:** ${preConsult.nurse_notes}`);
  }
  if (consultation?.content) {
    lines.push(`**Doctor's ${consultation.doc_type || 'consultation'} note (leading to admission):**\n${consultation.content}`);
  }
  if (admission.provisional_diagnosis) lines.push(`**Provisional diagnosis on admission:** ${admission.provisional_diagnosis}`);
  if (admission.admission_notes) lines.push(`**Admission notes:** ${admission.admission_notes}`);
  if ((admission.charges || []).length) {
    lines.push(`**Charges/procedures during stay:**\n${admission.charges.map(c => `- ${c.description}${c.quantity > 1 ? ` ×${c.quantity}` : ''}`).join('\n')}`);
  }
  return lines.join('\n\n');
}

// The IP Case Sheet (structured assessment, progress notes, medication
// orders, vitals, investigations, consults, care plan — see
// features/ip-case-sheet) is a second, richer source of what happened
// DURING this stay, distinct from buildCriticalInfoSummary above (which
// only knows what was recorded on the originating OP visit, before
// admission). Reusing fetchCaseSheet — the same read the Case Sheet
// workspace itself calls — means the discharge summary is drafted from
// exactly the structured record the doctor has been keeping, not a second
// re-typing of it.
function buildCaseSheetInfoSummary(cs: CaseSheet): string {
  const lines: string[] = [];

  if (cs.reason_for_admission) lines.push(`**Reason for admission:** ${cs.reason_for_admission}`);
  if (cs.working_diagnosis) lines.push(`**Working diagnosis:** ${cs.working_diagnosis}`);

  if (cs.active_problems?.length) {
    lines.push(`**Active problems:**\n${cs.active_problems.map(p => `- ${p.diagnosis}${p.status ? ` (${p.status})` : ''}`).join('\n')}`);
  }

  if (cs.allergies?.length) {
    lines.push(`**Allergies:** ${cs.allergies.map(a => a.substance).join(', ')}`);
  } else if (cs.allergy_status === 'reviewed-none') {
    lines.push(`**Allergies:** none known (reviewed)`);
  }

  const v = cs.latest_vitals;
  if (v) {
    const bits = [
      v.bp_systolic != null && `BP ${v.bp_systolic}/${v.bp_diastolic ?? '—'} mmHg`,
      v.pulse_rate != null && `pulse ${v.pulse_rate}/min`,
      v.spo2 != null && `SpO2 ${v.spo2}%`,
      v.temperature != null && `temp ${v.temperature}°C`,
      v.oxygen_delivery && v.oxygen_delivery !== 'room-air' && `O2 ${v.oxygen_delivery}${v.oxygen_flow_lpm ? ` ${v.oxygen_flow_lpm} L/min` : ''}`,
    ].filter(Boolean);
    if (bits.length) lines.push(`**Vitals at last recording (condition at discharge should reflect this):** ${bits.join(' · ')}`);
  }

  if (cs.medication_orders?.length) {
    lines.push(`**Medications active during the stay (review for continuation on discharge):**\n${
      cs.medication_orders.map(o => `- ${o.drug_name}${o.strength ? ` ${o.strength}` : ''}${o.route ? ` ${o.route}` : ''}${o.frequency_code ? ` ${o.frequency_code}` : ''}${o.is_prn ? ' PRN' : ''}`).join('\n')
    }`);
  }

  if (cs.investigations?.length) {
    lines.push(`**Investigations during the stay:**\n${
      cs.investigations.map(i => `- ${i.investigation_name}: ${i.status}${i.result_status ? `, result ${i.result_status}` : ''}`).join('\n')
    }`);
  }

  if (cs.consults?.length) {
    lines.push(`**Specialist consults:**\n${cs.consults.map(c => `- ${c.specialty_name || 'Specialist'}: ${c.status}${c.reason ? ` — ${c.reason}` : ''}`).join('\n')}`);
  }

  const openCare = (cs.care_plan || []).filter(a => a.status !== 'completed' && a.status !== 'cancelled');
  if (openCare.length) {
    lines.push(`**Open care-plan items (consider a follow-up plan for these):**\n${openCare.map(a => `- ${a.category}: ${a.description}`).join('\n')}`);
  }

  if (cs.last_progress_note?.assessment || cs.last_progress_note?.plan) {
    lines.push(`**Most recent physician note:**${cs.last_progress_note.assessment ? `\n${cs.last_progress_note.assessment}` : ''}${cs.last_progress_note.plan ? `\nPlan: ${cs.last_progress_note.plan}` : ''}`);
  }

  return lines.join('\n\n');
}

interface DischargeSummaryChatProps {
  admission: Admission;
  onApproved?: (content: string) => void;
}

export default function DischargeSummaryChat({ admission, onApproved }: DischargeSummaryChatProps) {
  const [msgs, setMsgs]         = useState<ChatMessage[]>([]);
  const [inp, setInp]           = useState('');
  const [loading, setLoading]   = useState(false);
  // genDoc is the summary as the AI last produced it (sent as ai_draft);
  // editDoc is what the doctor is editing and approves.
  const [genDoc, setGenDoc]     = useState<string | null>(null);
  const [editDoc, setEditDoc]   = useState('');
  // See ConsultationChat.tsx's own editDocRef comment — the ↻ "regenerate
  // section" button is a ProseMirror decoration widget that can reuse a
  // stale closure across rebuilds, so regenerateSection reads the document
  // through this ref rather than closing over editDoc directly.
  const editDocRef = useRef(editDoc);
  useEffect(() => { editDocRef.current = editDoc; }, [editDoc]);
  const [regeneratingKey, setRegeneratingKey] = useState<string | null>(null);
  const [manualMode, setManualMode] = useState(false);
  const [manualText, setManualText] = useState('');
  // 'ai' | 'manual' — which path produced the current genDoc, since
  // manualMode itself flips back to false the instant useManualDraft()
  // commits the draft (see below); approve() needs this to record the
  // correct generation_source rather than always defaulting to 'ai'.
  const [docSource, setDocSource] = useState<'ai' | 'manual'>('ai');
  const [approved, setApproved] = useState(false);
  const [approvedDoc, setApprovedDoc] = useState<{ id: number; uhid: string } | null>(null);
  const [approvedConsultationId, setApprovedConsultationId] = useState<number | null>(null); // for "Promote as hospital template"
  const [promoteState, setPromoteState] = useState<'idle' | 'busy' | 'done'>('idle');
  const [promoteError, setPromoteError] = useState('');
  // Whether the last promote created a brand-new profile (no active
  // Discharge Summary profile existed yet) vs. proposed an edit to an
  // existing one — only changes the confirmation wording.
  const [promoteCreatedNew, setPromoteCreatedNew] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [aiCfg, setAiCfg]       = useState(loadConfig);
  // Always on — the "Include in AI context"/"Patient information on file"
  // panel that used to toggle this was removed to give the note canvas more
  // room (it offered no real benefit: the fuller context is strictly better
  // for drafting, and the doctor still sees/edits the resulting document
  // either way).
  const includeContext = true;
  const [suggestions, setSuggestions] = useState<GenerationSuggestion[]>([]);
  const [appliedMemory, setAppliedMemory] = useState<AppliedMemoryItem[]>([]);
  const [showPreferences, setShowPreferences] = useState(false);
  // At most one learned-preference proposal per session, checked once right
  // after the first successful approval — mirrors ConsultationChat.tsx's own
  // proposal/proposalCheckedRef.
  const [proposal, setProposal] = useState<DoctorPreference | null>(null);
  const proposalCheckedRef = useRef(false);
  const [streamingText, setStreamingText] = useState<string | null>(null);
  // Whether the hospital's AI switch is on (ai_settings.ai_processing_enabled)
  // — null while unknown. Fails closed (false) so an unreachable check never
  // fires an AI call the hospital didn't ask for, and AssistantColumn shows
  // its plain dictation-only state instead of a composer that would just 403.
  const [aiEnabled, setAiEnabled] = useState<boolean | null>(null);
  // v3 change ribbon (docs/HMS_CONVERSATIONAL_AI.md §4) — snapshot right
  // before a turn/regenerate starts, and whether a prior draft already
  // exists this session (no ribbon on the very first fill).
  const preDocSnapshotRef = useRef<string | null>(null);
  const hasPriorDraftRef = useRef(false);
  const [changeSummary, setChangeSummary] = useState<SectionChange[] | null>(null);
  const [turnChanges, setTurnChanges] = useState<Map<number, SectionChange[]>>(new Map());
  const [latestChangeIndex, setLatestChangeIndex] = useState<number | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const [dockExpanded, setDockExpanded] = useState(false);
  // Resume support (see migration_discharge_summary_messages.sql) — whether
  // the one-time fetch of this admission's persisted transcript has
  // finished, and whether it actually found anything. The greeting/auto-draft
  // effect below waits on the former and skips entirely on the latter, so a
  // doctor navigating back to an in-progress discharge summary sees exactly
  // what they left, not a second greeting or a second AI draft.
  const [msgsLoading, setMsgsLoading] = useState(true);
  const restoredRef = useRef(false);
  // Guards the silent auto-draft (see the greeting effect below) against
  // firing twice for the same admission — same StrictMode-double-invoke
  // concern ConsultationChat.tsx's autoDraftedForRef documents.
  const autoDraftedForRef = useRef<number | null>(null);
  const documentEditorRef = useRef<DocumentEditorHandle>(null);
  const { width: assistantWidth, startResize } = useResizableWidth({
    key: ASSISTANT_WIDTH_KEY, defaultValue: DEFAULT_ASSISTANT_WIDTH, min: MIN_ASSISTANT_WIDTH, max: MAX_ASSISTANT_WIDTH,
  });
  // Structured record of the stay itself (assessment, progress notes,
  // medications, vitals, investigations, consults, care plan) — a second,
  // richer context source alongside the origin-visit one below. Best-effort:
  // an admission with no Case Sheet activity yet (or a transient fetch
  // failure) just means this block is empty, not an error the doctor has to
  // deal with.
  const [caseSheet, setCaseSheet] = useState<CaseSheet | null>(null);
  // Settles true once the fetch above resolves either way (data or none) —
  // distinct from `caseSheet` itself being null, which also means "still
  // loading". The auto-draft effect below waits on this so it doesn't fire
  // its one-shot attempt before the richer Case Sheet context has arrived.
  const [caseSheetLoaded, setCaseSheetLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchCaseSheet(admission.id)
      .then(cs => { if (!cancelled) setCaseSheet(cs); })
      .catch(() => { if (!cancelled) setCaseSheet(null); })
      .finally(() => { if (!cancelled) setCaseSheetLoaded(true); });
    return () => { cancelled = true; };
  }, [admission.id]);

  useEffect(() => {
    getAiSettings().then((s) => setAiEnabled(!!s.ai_processing_enabled)).catch(() => setAiEnabled(false));
  }, []);

  const provider = getActiveProvider(aiCfg);
  const modelId  = getActiveModel(aiCfg);
  // The AI has no other way to know the real admission date — without this,
  // nothing stops it from writing a plausible-looking but fabricated date
  // into the generated document's "Admission Date" line.
  const patientForPrompt = {
    name: admission.patient_name, age: admission.age, gender: admission.gender, uhid: admission.patient_uhid,
    admission_date: new Date(admission.admitted_at).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }),
    discharge_date: new Date().toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }),
  };
  // Origin-visit info (pre-admission) and Case Sheet info (during the stay)
  // are complementary, not overlapping — concatenated into one context block
  // rather than a second toggle, so the doctor isn't asked to manage two
  // near-identical checkboxes for what is really one "everything on file" idea.
  const criticalInfo = [buildCriticalInfoSummary(admission), caseSheet ? buildCaseSheetInfoSummary(caseSheet) : '']
    .filter(Boolean).join('\n\n');
  const hasCriticalInfo = criticalInfo.trim().length > 0;
  // Once the doctor has actually started the conversation, the greeting
  // (and whatever it already carries) is history — the toggle stops
  // reshaping messages already sent, same as you can't unsend a message.
  const conversationStarted = msgs.some(m => m.role === 'user');

  useEffect(() => {
    const refresh = () => setAiCfg(loadConfig());
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, []);

  // Resume: fetch this admission's persisted transcript exactly once, before
  // the greeting effect below decides whether to greet fresh or auto-draft
  // (see migration_discharge_summary_messages.sql — the same
  // resume-on-reload guarantee ConsultationChat.tsx already has via
  // consultation_messages, now admission-scoped since an IP admission has no
  // queue_id). A transient fetch error is treated the same as "nothing
  // persisted yet" — the doctor can still start fresh.
  useEffect(() => {
    let cancelled = false;
    getDischargeMessages(admission.id).then((rows: ChatMessage[]) => {
      if (cancelled || !rows.length) return;
      restoredRef.current = true;
      setMsgs(rows.map(r => ({ role: r.role, content: r.content })));
      const lastDraft = [...rows].reverse().find(r => r.role === 'assistant' && (r.content.includes(MANUAL_MARKER) || looksLikeDocument(r.content)));
      if (lastDraft) {
        hasPriorDraftRef.current = true;
        if (lastDraft.content.includes(MANUAL_MARKER)) {
          const cleaned = lastDraft.content.split('\n').filter(l => l.trim() !== MANUAL_MARKER).join('\n').trim();
          setGenDoc(cleaned); setEditDoc(cleaned); setDocSource('manual');
        } else {
          const cleaned = splitDraftReply(lastDraft.content).doc ?? cleanAIDraft(lastDraft.content);
          setGenDoc(cleaned); setEditDoc(cleaned); setDocSource('ai');
        }
        lastPersistedDocRef.current = lastDraft.content;
      }
    }).catch(() => { /* nothing persisted yet, or a transient error — start fresh */ })
      .finally(() => { if (!cancelled) setMsgsLoading(false); });
    return () => { cancelled = true; };
  }, [admission.id]);

  const persist = (role: ChatMessage['role'], content: string, extra: Record<string, unknown> = {}) =>
    addConsultationMessage({ admission_id: admission.id, role, content, doc_type: DOC_TYPE, ...extra }).catch(() => {});

  // Autosave: a chat turn (or the manual-draft commit) already persists the
  // document it produced, but a doctor filling a smart-field chip in place,
  // ticking "Remove unfilled", or just editing text directly in the note
  // never goes through either of those — without this, exactly that work is
  // what's lost on navigating away before approving. Debounced so a fast
  // typist doesn't write one row per keystroke; skipped whenever `editDoc`
  // already matches what's on record (the common case right after a chat
  // turn or a restore) so it doesn't duplicate that row a moment later.
  const lastPersistedDocRef = useRef<string | null>(null);
  useEffect(() => {
    if (!genDoc || approved || loading || editDoc === lastPersistedDocRef.current) return;
    const handle = setTimeout(() => {
      lastPersistedDocRef.current = editDoc;
      if (editDoc.trim()) persist('assistant', editDoc);
    }, 1200);
    return () => clearTimeout(handle);
  }, [editDoc, genDoc, approved, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Wait for the resume fetch above, and never overwrite a restored
    // conversation. The Case Sheet fetch is separately async and can resolve
    // after this effect's first run, so it's a dependency too — the greeting
    // is regenerated once it lands, picking up the richer context. But never
    // once the doctor has actually started typing: at that point the
    // greeting is history, same as includeContext's own toggle-lock above,
    // and a late-arriving fetch must not blow away an in-progress conversation.
    if (msgsLoading || restoredRef.current || conversationStarted) return;
    const days = Math.max(1, Math.ceil((Date.now() - new Date(admission.admitted_at).getTime()) / 86400000));
    const includeBlock = includeContext && hasCriticalInfo ? `${criticalInfo}\n\n` : '';
    const reviewNote = includeContext && hasCriticalInfo
      ? 'Please review the information above, correct anything, and add what\'s missing — especially treatment course during the stay, condition at discharge, and follow-up plan.'
      : 'Please describe the final diagnosis, treatment course, condition at discharge and follow-up plan.';
    const greeting = `Hello Doctor! Preparing the discharge summary for **${admission.patient_name}** (${admission.gender}, ${admission.age}y · UHID: ${admission.patient_uhid}).\n\n**Admission:** ${admission.ward_name}, bed ${admission.bed_number} — ${new Date(admission.admitted_at).toLocaleDateString('en-IN')} (${days} day${days === 1 ? '' : 's'} stay)\n\n${includeBlock}${reviewNote} I will generate a structured **Discharge Summary** for you.`;
    setMsgs([{ role: 'assistant', content: greeting }]);

    // Auto-populate the note on load rather than waiting for the doctor to
    // type a prompt — mirrors ConsultationChat.tsx's own D9 silent first
    // draft. Only when the hospital's AI switch is actually on; AI off
    // leaves the doctor to type or dictate, same as before this fix (there's
    // no deterministic no-AI skeleton for an admission — see file header
    // comment on why this screen has no capturedDataSkeleton.js equivalent).
    // Waits on caseSheetLoaded specifically (not just this effect's own
    // re-run on a `caseSheet` change) because autoDraftedForRef is a
    // one-shot guard — firing on an early run, before the Case Sheet's
    // richer context has arrived, would mean it never fires again with the
    // fuller picture.
    if (aiEnabled && caseSheetLoaded && autoDraftedForRef.current !== admission.id) {
      autoDraftedForRef.current = admission.id;
      send(FILL_FROM_CLINICAL_DATA, { silent: true });
    }
  }, [admission.id, includeContext, caseSheet, caseSheetLoaded, msgsLoading, aiEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  // silent: for the automatic first draft above — a UI-internal trigger, not
  // something the doctor typed, so it's sent to the model but never shown or
  // persisted as a chat bubble (matching ConsultationChat.tsx's own send()).
  const send = async (text: string, { silent = false }: { silent?: boolean } = {}) => {
    const typed = text.trim();
    if (!typed || loading) return;
    const userMsg: ChatMessage = { role: 'user', content: typed };
    if (!silent) setMsgs([...msgs, userMsg]);
    setInp('');
    if (!silent) persist('user', typed);
    setLoading(true); setSuggestions([]); setStreamingText('');
    setChangeSummary(null);
    setAppliedMemory([]);

    // With a summary open, the AI revises it as it stands — so the doctor's
    // own edits survive a follow-up request. The instruction is deliberately
    // CONDITIONAL ("if this asks you to change the summary... otherwise just
    // answer"), not the unconditional "revise it as asked and return the
    // complete summary" this screen used to send — that unconditional
    // wording is what made a genuine question ("what does LAMA mean?") get
    // silently swallowed into a re-sent, unchanged summary instead of
    // actually being answered (the exact bug docs/HMS_CONVERSATIONAL_AI.md
    // §3.2/§7 describes for the pre-v3 version of this screen). Letting the
    // model judge per message, rather than a client-side heuristic guessing
    // "is this a question," is the same choice ConsultationChat.tsx makes.
    const current = editDoc.trim();
    const outgoing: AIMessage[] = current
      ? [...msgs, {
          role: 'user',
          content: `${typed}\n\nCurrent discharge summary, for reference:\n\n${current}\n\nIf this message asks you to change the summary, revise it and return the complete updated summary, starting with the usual "AI-ASSISTED DRAFT" marker line — keep everything else exactly as written, including the doctor's own edits. If it's a question or a comment that doesn't call for a summary change (e.g. asking what something means, or reporting a problem), just answer or respond directly instead — do not repeat, rewrite or otherwise touch the summary, and do not include the marker.`,
        }]
      : [...msgs, userMsg];

    // Snapshot right before this turn touches anything, so the change ribbon
    // and single-level Undo have something to revert to.
    preDocSnapshotRef.current = editDoc;
    const newMsgIndex = msgs.length;
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const result = await generateDocument({
        model: modelId,
        patient: patientForPrompt,
        messages: outgoing,
        doc_type: DOC_TYPE,
        temperature: aiCfg.temperature,
        max_tokens: aiCfg.maxTokens,
        // Sent as a dedicated field, not just folded into the greeting chat
        // bubble — the silent auto-draft (the greeting effect above) fires
        // before that bubble has landed in `msgs`' closured value, so relying
        // on chat history alone left the very first turn with no clinical
        // data to draft from.
        extra_context: includeContext && hasCriticalInfo ? criticalInfo : undefined,
        capabilities: ['smart_fields', 'suggestions', 'chat_reply', 'memory'],
      }, {
        signal: controller.signal,
        // The reply streams straight into the note canvas itself, same as
        // Consultation Notes' v3 layout — the note is made read-only below
        // while `loading` is true, so this never fights a concurrent edit.
        onDelta: (soFar) => {
          setStreamingText(soFar);
          const live = splitDraftReply(soFar);
          if (live.doc !== null) { setGenDoc(live.doc); setEditDoc(live.doc); }
        },
      });

      const reply = result.content;
      setMsgs(p => [...p, { role: 'assistant', content: reply }]);
      persist('assistant', reply, { provider: aiCfg.provider });
      setSuggestions(result.suggestions);
      setAppliedMemory(result.applied_memory ?? []);
      const { doc } = splitDraftReply(reply);
      if (doc !== null || looksLikeDocument(reply)) {
        const cleaned = doc ?? cleanAIDraft(reply);
        setGenDoc(cleaned); setEditDoc(cleaned); setApproved(false); setDocSource('ai');
        // What the autosave effect compares against is `editDoc` (the
        // cleaned text just set above), not the raw `reply` just persisted —
        // matching the two here is what keeps that effect from re-persisting
        // a near-duplicate row a moment after every turn.
        lastPersistedDocRef.current = cleaned;
        // Change ribbon/per-turn chips only ever compare against a REAL
        // prior draft — never on the very first fill.
        if (hasPriorDraftRef.current && preDocSnapshotRef.current != null) {
          const changes = diffSections(preDocSnapshotRef.current, cleaned);
          if (changes.length) {
            setChangeSummary(changes);
            setTurnChanges(prev => new Map(prev).set(newMsgIndex, changes));
            setLatestChangeIndex(newMsgIndex);
          }
        }
        hasPriorDraftRef.current = true;
      }
    } catch (e) {
      const aborted = (e as { name?: string } | null)?.name === 'AbortError';
      if (aborted) {
        const note = '⏹ Stopped — the summary reflects what was written so far.';
        setMsgs(p => [...p, { role: 'assistant', content: note }]);
        persist('assistant', note);
      } else {
        const message = e instanceof Error ? e.message : String(e);
        setMsgs(p => [...p, { role: 'assistant', content: `⚠ Error: ${message}` }]);
      }
    }
    abortRef.current = null;
    setStreamingText(null);
    setLoading(false);
  };

  const sendMessage = () => send(inp);
  const stopGeneration = () => abortRef.current?.abort();

  const handleRemember = async (suggestion: Extract<GenerationSuggestion, { type: 'remember' }>) => {
    setSuggestions((prev) => prev.filter((s) => s !== suggestion));
    try {
      const saved = await createPreference({ docType: DOC_TYPE, category: 'content', statement: suggestion.statement, origin: 'chat' });
      toast({
        variant: 'success', title: 'Remembered ✓', description: suggestion.statement, duration: 10000,
        action: <ToastAction altText="Undo" onClick={() => { deletePreference(saved.id).catch(() => {}); }}>Undo</ToastAction>,
      });
    } catch (e) {
      toast({ variant: 'danger', title: 'Could not save this preference', description: e instanceof Error ? e.message : undefined });
    }
  };

  // Rewrites just the clicked section, leaving every other line byte-for-
  // byte as it was — see ConsultationChat.tsx's own regenerateSection.
  const regenerateSection = async ({ key, heading }: HeadingSection) => {
    if (regeneratingKey) return;
    setRegeneratingKey(key);
    try {
      const current = editDocRef.current;
      preDocSnapshotRef.current = current;
      const instruction = `Regenerate only the "${heading}" section below. Return the complete discharge summary with every other section — every other line, exactly as written, including the doctor's own edits — kept byte-for-byte identical. Do not rename, remove, reorder or add any section.\n\nCurrent discharge summary:\n\n${current}`;
      const result = await generateDocument({
        model: modelId,
        patient: patientForPrompt,
        messages: [{ role: 'user', content: instruction }],
        doc_type: DOC_TYPE,
        temperature: aiCfg.temperature,
        max_tokens: aiCfg.maxTokens,
        capabilities: ['smart_fields', 'memory'],
      });
      const cleaned = cleanAIDraft(result.content);
      const spliced = spliceSectionFromReply(current, cleaned, key);
      if (spliced == null) {
        toast({ variant: 'danger', title: 'Could not regenerate that section', description: "The AI's reply didn't include a matching section — nothing changed." });
        return;
      }
      setGenDoc(spliced); setEditDoc(spliced);
      setChangeSummary([{ key, heading, kind: 'changed' }]);
      setLatestChangeIndex(null); // not a transcript turn — only the ribbon shows this one
      toast({ variant: 'success', title: `Regenerated: ${heading}` });
    } catch (e) {
      toast({ variant: 'danger', title: 'Could not regenerate that section', description: e instanceof Error ? e.message : undefined });
    } finally {
      setRegeneratingKey(null);
    }
  };

  const useManualDraft = () => {
    const content = stripTitleEcho(manualText.trim()).join('\n').trim();
    if (!content) return;
    setGenDoc(content); setEditDoc(content); setApproved(false); setManualMode(false); setDocSource('manual');
    hasPriorDraftRef.current = true;
    const marked = `${MANUAL_MARKER}\n\n${content}`;
    setMsgs(p => [...p, { role: 'assistant', content: marked }]);
    persist('assistant', marked);
    lastPersistedDocRef.current = content; // matches what editDoc was just set to, above
  };

  const keepChanges = () => setChangeSummary(null);
  const undoChanges = () => {
    if (preDocSnapshotRef.current != null) {
      setGenDoc(preDocSnapshotRef.current);
      setEditDoc(preDocSnapshotRef.current);
    }
    setChangeSummary(null);
    if (latestChangeIndex != null) {
      setTurnChanges(prev => { const next = new Map(prev); next.delete(latestChangeIndex); return next; });
      setLatestChangeIndex(null);
    }
  };
  const jumpToSection = (key: string) => { documentEditorRef.current?.scrollToSection(key); };

  // AI-off assistant column's plain dictation — appended straight to the
  // summary, no AI round-trip.
  const dictateIntoNote = (text: string) => {
    setEditDoc((d) => (d.trim() ? `${d}\n${text}` : text));
    setGenDoc((d) => (d && d.trim() ? `${d}\n${text}` : text));
  };

  const statusLabel = !loading ? '' : (streamingText === '' || streamingText === null)
    ? 'Thinking…'
    : splitDraftReply(streamingText).doc === null ? 'Responding…' : 'Writing the summary…';

  const unfilled = useMemo(() => unfilledFieldLabels(editDoc), [editDoc]);

  const insertables = useMemo<InsertableValue[]>(() => {
    const items: InsertableValue[] = [
      { key: 'today', label: "Today's date", value: formatDocDate(new Date()) },
      { key: 'admitted', label: 'Admission date', value: formatDocDate(new Date(admission.admitted_at)) },
      { key: 'follow-up', label: 'Follow-up date (to fill in)', value: 'Follow-up: {{date|Follow-up date}}' },
    ];
    if (admission.provisional_diagnosis) items.push({ key: 'dx', label: 'Provisional diagnosis', value: admission.provisional_diagnosis });
    const doctorName = getAuthUser()?.full_name;
    if (doctorName) items.push({ key: 'doctor', label: 'Doctor name', value: doctorName });
    return items;
  }, [admission.admitted_at, admission.provisional_diagnosis]);

  const approve = async () => {
    const content = editDoc;
    if (!content.trim()) return;
    if (unfilled.length) {
      setSaveError(`Fill in or remove ${unfilled.length === 1 ? 'this field' : 'these fields'} first: ${unfilled.join(', ')}`);
      return;
    }
    setSaveError('');
    try {
      const saved = await saveConsultation({
        patient_id: admission.patient_id,
        queue_id: null,
        // Phase 5 unification (docs/HMS_V4_IP_CASE_SHEET_INDIA_DESIGN.md
        // §6.11): lets routes/consultation.js's POST /save also sync this
        // approved text's narrative sections into ip_discharge_summaries as
        // a draft, so the Case Sheet's Discharge tab reflects what was just
        // drafted here without the doctor re-typing it. Everything else
        // about this call — the .docx, Doctor AI Memory, streaming — is
        // unchanged.
        admission_id: admission.id,
        doc_type: DOC_TYPE,
        ai_draft: genDoc,
        final_doc: content,
        // Only AI-generated documents are measured as AI edits; a manually-
        // written one compared against itself would read as "AI draft
        // accepted unchanged" (routes/consultation.js).
        generation_source: docSource,
        approved: true,
        approved_by: getAuthUser()?.full_name || 'doctor',
      });
      setApproved(true);
      setSuggestions([]);
      setChangeSummary(null);
      if (saved.document_id) setApprovedDoc({ id: saved.document_id, uhid: saved.document_uhid });
      setApprovedConsultationId(saved.id);
      setPromoteState('idle');
      setPromoteError('');
      const draftStatus = saved.discharge_summary_draft?.status;
      let syncNote = '';
      if (draftStatus === 'created' || draftStatus === 'updated') {
        syncNote = '\n\nSynced to the structured discharge summary in the Case Sheet\'s Discharge tab — add the medications/follow-up and sign it there.';
      } else if (draftStatus === 'skipped_signed') {
        syncNote = '\n\n⚠ The Case Sheet already has a *signed* discharge summary for this admission — this text was not synced there. Use Amend in the Discharge tab if it needs correcting.';
      }
      const note = `✅ **Discharge Summary** approved and saved to ${admission.patient_name}'s record. _(Generated by ${provider.name} / ${modelId})_${syncNote}`;
      setMsgs(p => [...p, { role: 'assistant', content: note }]);
      onApproved?.(content);

      if (!proposalCheckedRef.current) {
        proposalCheckedRef.current = true;
        getNextProposal(DOC_TYPE).then((r) => setProposal(r.proposal)).catch(() => {});
      }
    } catch (e) { setSaveError(e instanceof Error ? e.message : String(e)); }
  };

  // "Promote as hospital template" — once a doctor is happy with a summary
  // after refining it through the conversation, they can propose it as a
  // de-identified example on the hospital's active Discharge Summary AI
  // Assistant Role profile — or, if this hospital has no active Discharge
  // Summary profile yet, propose THIS document as a brand-new one. Same
  // route Consultation Notes uses (POST /api/consultation/:id/promote-to-
  // profile), now generic over doc_type — see backend/routes/consultation.js.
  // Never takes effect on its own either way: an Executive Director must
  // approve it first, same governance as an Admin's own manual edit/
  // activation there.
  const promoteToProfile = async () => {
    if (!approvedConsultationId) return;
    setPromoteState('busy');
    setPromoteError('');
    try {
      const result = await promoteConsultationToProfile(approvedConsultationId);
      setPromoteCreatedNew(!!result?.created);
      setPromoteState('done');
    } catch (e) {
      setPromoteState('idle');
      setPromoteError(e instanceof Error ? e.message : String(e));
    }
  };

  const assistantColumnCommonProps = {
    title: 'Discharge assistant',
    // The shared transcript's own unmarked-document fallback only knows
    // Consultation Notes' SOAP vocabulary — without this, a reply that
    // omits the "--- AI-ASSISTED DRAFT ---" marker (routine on the silent
    // first turn above) read as plain chat and the whole discharge summary
    // got echoed into the bubble, duplicating the note canvas.
    isDocumentText: looksLikeDocument,
    aiEnabled, approved, msgs, msgsLoading, loading, streamingText, statusLabel,
    changesByIndex: turnChanges, latestChangeIndex,
    onJumpToSection: jumpToSection, onUndoTurn: undoChanges,
    suggestions, onSendSuggestion: send, onRemember: handleRemember, appliedMemory,
    onOpenPreferences: () => setShowPreferences(true), proposal, onProposalDone: () => setProposal(null),
    onFillFromClinicalData: () => send(FILL_FROM_CLINICAL_DATA),
    // No completeness-nudges engine for discharge summaries yet (see file
    // header comment) — always empty, so the AI-off state reads "nothing to
    // flag" rather than a real checklist.
    nudges: [],
    onDictateIntoNote: dictateIntoNote,
    inp, onInpChange: setInp, onSend: sendMessage,
    onDictateIntoComposer: (t: string) => setInp(v => (v ? v + ' ' : '') + t),
    // No full-conversation "Record conversation" flow for discharge (the
    // pre-v3 version of this screen never had one either) — dictation into
    // the composer/note still works via the mic buttons ConsultationComposer
    // and the AI-off footer always render.
    canRecord: false, recording: false, onToggleRecording: () => {},
    latestLine: null, dockExpanded, onToggleDock: () => setDockExpanded(v => !v),
    onStop: stopGeneration,
  };

  return (
    <div className="flex h-full flex-col gap-2">
      {approved && (
        <div className="flex flex-wrap items-center gap-2.5 rounded-[var(--radius)] border border-success/40 bg-success-soft px-3 py-2">
          <span className="text-[12px] font-semibold text-success-ink">✅ Discharge summary approved and saved.</span>
          {approvedDoc && (
            <div className="ml-auto">
              <DocumentPreviewButtons uhid={approvedDoc.uhid} docId={approvedDoc.id} label="Discharge summary" />
            </div>
          )}
        </div>
      )}
      {approved && docSource === 'ai' && approvedConsultationId && (
        <div className="flex flex-wrap items-center gap-2.5 rounded-[var(--radius)] border border-line bg-card px-3 py-2 text-[12px]">
          {promoteState === 'done' ? (
            <span className="font-medium text-success-ink">
              {promoteCreatedNew
                ? '✅ Created and proposed as the new active "Discharge Summary" AI Assistant Role profile — an Executive Director will review it in Admin Panel → AI Role.'
                : '✅ Proposed — an Executive Director will review it in Admin Panel → AI Role.'}
            </span>
          ) : (
            <>
              <span className="text-ink-muted">Happy with this summary after the refinements above?</span>
              <Button type="button" size="sm" variant="ghost" className="ml-auto" disabled={promoteState === 'busy'} onClick={promoteToProfile}>
                {promoteState === 'busy' ? 'Proposing…' : 'Promote as hospital template'}
              </Button>
            </>
          )}
          {promoteError && <span className="w-full text-danger-ink">⚠ {promoteError}</span>}
        </div>
      )}
      {approved && proposal && (
        <div className="rounded-[var(--radius)] border border-line bg-card px-3 py-2">
          <ProposalCard proposal={proposal} onDone={() => setProposal(null)} />
        </div>
      )}

      <div className="flex min-h-[480px] gap-0 xl:h-[calc(100vh-420px)]">
        {/* Assistant column — left, resizable (≥1280px only; the docked
            narrow-screen variant renders separately, below). Matches the
            OP Consultation Notes v3 layout (conversation left, note right). */}
        <div className="hidden shrink-0 pr-3 xl:block" style={{ width: assistantWidth }}>
          <AssistantColumn variant="column" {...assistantColumnCommonProps} />
        </div>
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize the assistant column"
          onMouseDown={startResize}
          className="hidden w-3 shrink-0 cursor-col-resize items-center justify-center xl:flex"
        >
          <div className="h-10 w-1 rounded-full bg-border" />
        </div>
        {/* Note canvas */}
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-[var(--radius)] border border-line bg-card">
          {!approved && !genDoc && (
            <div className="flex shrink-0 justify-end border-b border-line px-3 py-1.5">
              <button type="button" className="text-[11.5px] font-semibold text-teal hover:underline" onClick={() => setManualMode(m => !m)}>
                {manualMode ? '💬 Use AI chat instead' : '✍ Write manually'}
              </button>
            </div>
          )}
          {manualMode && !approved ? (
            <div className="flex flex-1 min-h-0 flex-col gap-2.5 overflow-y-auto p-3.5">
              <div className="text-[12px] text-ink-muted">Write the Discharge Summary directly — no AI involved.</div>
              <DocumentEditor value={manualText} onChange={setManualText} insertables={insertables} ariaLabel="Discharge Summary (manual)" />
              <button type="button" className="btn-primary self-end" disabled={!manualText.trim()} onClick={useManualDraft}>
                Use this as the document →
              </button>
            </div>
          ) : genDoc && !approved ? (
            <>
              {changeSummary && <ChangeRibbon changes={changeSummary} onJump={jumpToSection} onKeep={keepChanges} onUndo={undoChanges} />}
              <div className="min-h-0 flex-1 overflow-y-auto p-2">
                <DocumentEditor
                  ref={documentEditorRef} value={editDoc} onChange={setEditDoc} insertables={insertables}
                  onRegenerateSection={regenerateSection} readOnly={loading || !!regeneratingKey} ariaLabel="Discharge Summary"
                />
              </div>
            </>
          ) : approved ? (
            <div className="flex flex-1 items-center justify-center p-6 text-center text-[12.5px] text-ink-muted">
              ✅ This summary was approved — see the download link above.
            </div>
          ) : (
            <div className="flex flex-1 items-center justify-center p-6 text-[12.5px] text-ink-muted">
              {msgsLoading || (aiEnabled && loading) ? 'Preparing the discharge summary…' : 'The discharge summary will appear here.'}
            </div>
          )}
        </div>
      </div>
      {/* Docked narrow-screen (<1280px) variant. */}
      <AssistantColumn variant="dock" {...assistantColumnCommonProps} />

      {saveError && <div className="rounded-[var(--radius)] border border-danger/30 bg-danger-soft px-3 py-2 text-[12px] text-danger-ink">⚠ Could not save: {saveError}</div>}

      {genDoc && !approved && (
        <div className="flex flex-wrap items-center gap-2.5 rounded-[var(--radius)] border border-warn bg-warn-soft px-3 py-2">
          <span style={{ fontSize: 12, color: '#92400E', fontWeight: 600 }}>⚡ Discharge summary ready — awaiting doctor approval</span>
          {unfilled.length > 0 && (
            <span style={{ fontSize: 12, color: '#B45309' }}>
              {unfilled.length} field{unfilled.length === 1 ? '' : 's'} to fill ·{' '}
              <button type="button" onClick={() => setEditDoc(removeAllPlaceholders(editDoc))}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12, color: 'var(--teal)', fontWeight: 600 }}>
                Remove unfilled
              </button>
            </span>
          )}
          <div className="ml-auto">
            <Button type="button" size="sm" variant="success" disabled={unfilled.length > 0 || !editDoc.trim()} onClick={approve}
              title={unfilled.length ? `Fill in or remove: ${unfilled.join(', ')}` : undefined}>
              ✅ Approve & use
            </Button>
          </div>
        </div>
      )}

      <MyAiPreferencesPanel open={showPreferences} onOpenChange={setShowPreferences} />
    </div>
  );
}
